import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Shield, Mail, KeyRound } from "lucide-react";

export default function AdminLogin() {
  const { adminLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("sth_remember_email");
    if (saved) setEmail(saved);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pin) return;
    setLoading(true);
    try {
      await adminLogin(email.toLowerCase().trim(), pin, remember);
      toast.success("Signed in");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(26,22,19,0.72), rgba(26,22,19,0.9)), url('https://images.unsplash.com/photo-1787637607060-10fdc42ec51d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxoaXN0b3JpYyUyMGhlcml0YWdlJTIwYnJpY2slMjB0b3duJTIwaGFsbCUyMGFyY2hpdGVjdHVyZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc5MTAyMTE5MHww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md rounded-3xl border border-amber-900/20 bg-white/95 p-8 shadow-2xl">
          <div className="mb-6 flex flex-col items-center gap-3">
            <div className="rounded-full bg-amber-50 p-3 ring-1 ring-amber-200">
              <Shield className="h-7 w-7 text-amber-800" />
            </div>
            <h1 className="font-serif text-3xl font-semibold text-stone-900">Administration</h1>
            <p className="text-sm text-stone-600">Staff access only</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-stone-700">
                <Mail className="h-4 w-4" /> Email
              </label>
              <input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 outline-none focus:border-amber-700 focus:ring-2 focus:ring-amber-200"
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-stone-700">
                <KeyRound className="h-4 w-4" /> PIN
              </label>
              <input
                data-testid="admin-pin-input"
                type="password"
                inputMode="numeric"
                maxLength={10}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 tracking-widest outline-none focus:border-amber-700 focus:ring-2 focus:ring-amber-200"
                placeholder="••••"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input
                type="checkbox"
                data-testid="admin-remember-checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-stone-300 text-amber-700 focus:ring-amber-500"
              />
              Remember my email
            </label>

            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="w-full rounded-xl bg-stone-900 py-3 font-semibold text-white transition hover:bg-stone-800 disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-stone-600 underline-offset-4 hover:text-stone-900 hover:underline">
              ← Back to resident portal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
